import { BaseScreen, waitFor } from '@goodboydigital/astro';
import { Container, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { RectangleButton } from '../ui/buttons/RectangleButton';
import { Logo } from '../ui/Logo';
import { RankPanel } from '../ui/RankPanel';
import { RewardsPanel } from '../ui/RewardsPanel';
import { Browser } from '../utils/Browser';

export class HomeScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public playButton!: RectangleButton;
    public garageButton!: RectangleButton;
    public settingsButton!: NineSliceButton;

    private rewardsPanel!: RewardsPanel;
    private rankPanel!: RankPanel;

    constructor(app: App)
    {
        super(app);
        this.app = app;
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);
        this.view.sortableChildren = true;
        
        this.playButton = new RectangleButton({
            text: 'homeScreen_play',
            bg: 'magenta',
            w: 350,
            h: 130,
            fontSize: 70,
        });
        this.view.addChild(this.playButton.view);
        this.playButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_RACE_CLICK);
            this.app.controller.playNextRace();
        });

        this.garageButton = new RectangleButton({
            text: 'homeScreen_garage',
            bg: 'yellow',
            w: 350,
            h: 130,
            fontSize: 70,
            wobble: true,
        });
        this.view.addChild(this.garageButton.view);
        this.garageButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_CUSTOMISE_CLICK);
            this.app.controller.toGarage();
        });

        const settingsIcon = Sprite.from(this.app.assets.getTexture('icon-settings'));

        this.settingsButton = new NineSliceButton({
            icon: settingsIcon,
            accessibleTitle: 'Back',
            w: settingsIcon.width * 1.5,
            h: settingsIcon.height * 1.5,
            clickAudio: 'btn-settings-click',
        });
        this.view.addChild(this.settingsButton.view);
        this.settingsButton.onPress.connect(() =>
        {
            Browser.lockOrientation('landscape');
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_SETTINGS_CLICK);
            this.app.controller.toSettings(false);
        });
        this.view.addChild(this.settingsButton.view);

        this.rewardsPanel = new RewardsPanel(this.app);
        this.view.addChild(this.rewardsPanel.view);

        this.rankPanel = new RankPanel(this.app);
        this.view.addChild(this.rankPanel.view);
        
    }

    async show(): Promise<void>
    {
        this.resize(this.width, this.height);

        this.view.visible = true;
        this.playButton.view.visible = false;
        this.garageButton.view.visible = false;

        Shared.game.soundSystem.playMusic('menu-music');

        if (!this.app.user.onTutorial() && !Settings.browserLite && Settings.isApp()) this.rewardsPanel.open();
        if (!this.app.user.onTutorial() && !Settings.browserLite) this.rankPanel.open();
        
        this.showButtons();
        
        Shared.game.level.countChunkCountInLevels();

        this.app.analytics.trackState(AnalyticsState.HOMEPAGE);
    }

    private async showButtons(): Promise<void>
    {
        if(Settings.browserLite)
        {
            this.playButton.show(true, 0.3);
        }
        else
        {
            const racesPlayed = this.app.user.getRacesPlayed();

            if (racesPlayed) await this.playButton.show(true, 0.3);
            this.garageButton.show(true, racesPlayed ? 0 : 0.3);
        }
        
    }

    async hide(): Promise<void>
    {
        this.rewardsPanel.close();
        this.rankPanel.close();
        this.playButton.hide(true);
        this.garageButton.hide(true);
        await waitFor(200);
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        if(Settings.browserLite)
        {
            this.playButton.view.position.set(
                this.app.stageCenter.x,
                this.app.stageBottom - 90,
            );
        }
        else
        {
            this.playButton.view.position.set(
                this.app.user.getRacesPlayed() ? this.app.stageCenter.x - 200 : this.app.stageCenter.x,
                this.app.stageBottom - 90,
            );
    
            this.garageButton.view.position.set(
                this.app.user.getRacesPlayed() ? this.app.stageCenter.x + 200 : this.app.stageCenter.x,
                this.app.stageBottom - 90,
            );
        }

        this.settingsButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
        
        this.rewardsPanel.view.x = this.app.stageRight - (this.rewardsPanel.width / 2) - 20;
        this.rewardsPanel.view.y = h / 2;

        this.rankPanel.view.x = this.app.stageLeft + (this.rankPanel.width / 2) + 20;
        this.rankPanel.view.y = h / 2;
    }
}
